import { useEffect, useState } from 'react';
import { FileUpload } from '../DataMaintenance/FileUpload';
import { t } from 'i18next';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Button,
  Tooltip,
  IconButton,
  Badge,
} from '@mui/material';
import {
  ECellType,
  ECommonDialogType,
  TableHeaders,
  foreignKeysForTable,
  initialSortField,
  EDataMaintenanceType,
  EDataTableType,
  EModuleType,
  EActionType,
  RecordLength,
  deleteFields,
  FillFieldsWithValueAsNo,
  ECompareStatusCellValues,
  EApplicability,
  MaxDeleteCount,
  EStatus,
  EStatusColor,
} from '../../data/Constants';
import {
  DataGrid,
  gridStringOrNumberComparator,
  GridColDef,
  GridCellParams,
  GridRenderCellParams,
  GridValidRowModel,
  FilterPanelTrigger,
  Toolbar,
  ColumnsPanelTrigger,
  GridRowSelectionModel,
  ToolbarButton,
  GridViewColumnIcon,
  GridFilterListIcon,
  useGridApiContext,
  useGridSelector,
  gridFilterModelSelector,
} from '@mui/x-data-grid';
import { countryCodes } from '../../localization';
import { ExportDetails } from '../ExportDetails';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CompareIcon from '@mui/icons-material/Compare';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { amber, green, red } from '@mui/material/colors';
import { formatDateString, getMaxDate } from '../../services/DataHelperFunctions';
import { AppDispatch, AppState, AppStore } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogMessage } from '../../store/states/DialogSlice';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import { LinearProgressBar } from './LinearProgressBar';
import type { SelectChangeEvent } from '@mui/material/Select';

import type {
  IHandlePageChangeProps,
  IMaintenanceTableProps,
  IPropertyDefault,
  ITableHeaders,
  IDataTableRow,
  IStateData,
  IOpenDialogFn,
  IHandleSearchProps,
  IHandleRecordsChangeProps,
  IHandleModuleTypeChangeProps,
  IHandleRefreshProps,
  IDisableDeleteBtnProps,
  ISearchBarProps,
  ICustomToolbarProps,
  IButtonSectionProps,
  IRenderButtonSectionProps,
  IImageData,
  IDeleteFieldConfig,
  IPaginationRequest,
} from '../../../types';

const displayImport = {
  [EDataMaintenanceType.SalesText]: true,
  [EDataMaintenanceType.Images]: false,
  [EDataMaintenanceType.ProductHierarchy]: true,
  [EDataMaintenanceType.Prices]: true,
  [EDataMaintenanceType.LanguageAssociation]: false,
  [EDataMaintenanceType.MarketAssociation]: false,
};

const renderTooltipWithIcon = (
  val: string,
  col: ITableHeaders,
  params: GridRenderCellParams,
  openDialog: IOpenDialogFn
) => (
  <>
    <Tooltip title={val} placement="bottom" arrow>
      <span className="overflow-hidden overflow-ellipsis">{val}</span>
    </Tooltip>
    {val && col.hasIcon && renderIconButton(params, col, openDialog)}
  </>
);
const renderUpdateCell = (value: string) => {
  const parts = value.includes('/') ? value.split('/') : null;
  return parts ? (
    <span className="overflow-hidden overflow-ellipsis updateCell">
      <span className="removed">{parts[0]}</span>/ <span className="added">{parts[1]}</span>
    </span>
  ) : (
    <span className="overflow-hidden overflow-ellipsis">{value}</span>
  );
};

const renderSyncStatus = (syncStatus: string) => {
  const syncData = JSON.parse(syncStatus) as Record<string, string>;
  if (!syncData) {
    return EStatusColor.Error;
  }

  const allKeysHaveValues = Object.values(syncData).every((val) => val.trim() !== '');
  return allKeysHaveValues ? EStatusColor.Success : EStatusColor.Warning;
};

//This method return the property default value
function getDefaultValue(value: IPropertyDefault) {
  return value ? value.name : '';
}

//This method renders the value to the  data table cell based on ECellType
const valueGetterData = (header: ITableHeaders, params: GridValidRowModel) => {
  let val: string | IPropertyDefault = '';
  if (header.type === ECellType.ObjectChips || header.type === ECellType.ArrayChips) {
    val = params[header.field]
      ? params[header.field]
          .map((role: { code?: string; name?: string }) => role.code || role.name || role)
          .join(', ')
      : '';
    return val;
  } else if (header.type === ECellType.Object) {
    val = params[header.field];
    return getDefaultValue(val as IPropertyDefault);
  } else if (header.type === ECellType.Images) {
    val = params[header.field].map((role: { type: string }) => role.type).join(', ');
    return val;
  } else if (header.type === ECellType.Date) {
    val = formatDateString(params[header.field]);
    return val;
  } else if (header.type === ECellType.MaxDate) {
    const datesArr = params[header.key as string].map(
      (data: { modifiedOn: string }) => data.modifiedOn
    );
    return getMaxDate(datesArr);
  } else if (typeof params[header.field] === 'boolean') {
    return params[header.field] ? t('labels.yes') : t('labels.no');
  } else {
    return params[header.field];
  }
};

//This method return the class name for the dataTable columns
const getHeaderClass = (type: string, header: ITableHeaders) => {
  if (
    (type === EDataTableType.PropertyAssociation ||
      type === EDataTableType.FeaturePropertyAssociation) &&
    header?.applicability?.includes(EApplicability.Master)
  ) {
    return 'property-header';
  }
  return '';
};

const createGridColumns = (
  tableHeaders: ITableHeaders[],
  openDialog: IOpenDialogFn,
  uniqueKey: string,
  type: string,
  props: IMaintenanceTableProps
) => {
  return tableHeaders.map((header) => {
    return {
      headerClassName: getHeaderClass(type, header),
      field: header.field,
      headerName: header.hasNoTranslation ? header.field : t('labels.' + header.field), //To assign our own custom value in dropdown of filter button in datagrid
      sortable: header.sort,
      width: header.width,
      type: header.type,
      filterable: header.filter !== false,
      cellClassName: (params: GridCellParams) => {
        //To style the cells of individual column
        if (FillFieldsWithValueAsNo.includes(header.field) && !params.row[header.field]) {
          return 'cellWithValueAsNo';
        } else if (params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Added) {
          return 'cellStylesPH' + ECompareStatusCellValues.Added;
        } else if (
          params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Deleted
        ) {
          return 'cellStylesPH' + ECompareStatusCellValues.Deleted;
        } else if (
          params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Updated
        ) {
          return params.row[ECompareStatusCellValues.ModifiedCells]
            ?.map((modValue: string) => modValue.toLowerCase())
            .includes(header.field.toLowerCase())
            ? 'cellStylesPH' + ECompareStatusCellValues.Updated
            : '';
        } else {
          return '';
        }
      },
      renderHeader: () => {
        //renderHeader is used to render custom component or styling
        return (
          <span className="searchIconForColumn">
            {header.searchable ? <SearchIcon color="primary" /> : null}
            {header.hasNoTranslation ? header.field : t('labels.' + header.field)}
          </span>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        //renderCell is used to render custom component or styling
        return createCell(params, header, openDialog, uniqueKey, type as EDataTableType, props);
      },
      valueGetter: (_: unknown, params: GridValidRowModel) => {
        //valueGetter is used to return values to filter function in datagrid
        return valueGetterData(header, params);
      },
      sortComparator: gridStringOrNumberComparator, // To sort the table as we are using renderCell
    };
  });
};

const handleSearch = (props: IHandleSearchProps) => {
  const { newVal, recordsPerPage, token, getDataList, setPage, productId, dispatch } = props;
  dispatch(
    getDataList({ token, page: 1, limit: recordsPerPage, searchCode: newVal, productId: productId })
  );
  setPage(1);
};

const handlePageChange = (handlePageChangeProps: IHandlePageChangeProps) => {
  const {
    value,
    recordsPerPage,
    searchValue,
    stateData,
    setFilteredData,
    productId,
    props,
    dispatch,
  } = handlePageChangeProps;
  if ((!searchValue && stateData.data[value]) || (searchValue && stateData.searchData[value])) {
    if (searchValue) {
      setFilteredData(stateData.searchData[value]);
    } else {
      setFilteredData(stateData.data[value]);
    }
  } else {
    dispatch(
      props.getDataList({
        token: props.token,
        page: value,
        limit: recordsPerPage,
        searchCode: searchValue,
        productId: productId,
      })
    );
  }
};

const handleRecordsChange = (handleRecordsChangeProps: IHandleRecordsChangeProps) => {
  const { records, searchValue, setPage, productId, props, dispatch } = handleRecordsChangeProps;
  dispatch(
    props.getDataList({
      token: props.token,
      page: 1,
      limit: records,
      searchCode: searchValue,
      productId: productId,
    })
  );
  setPage(1);
};

const handleRefresh = (handleRefreshProps: IHandleRefreshProps) => {
  const { props, page, recordsPerPage, productId, dispatch } = handleRefreshProps;
  dispatch(
    props.getDataList({
      token: props.token,
      page: page,
      limit: recordsPerPage,
      searchCode: null as unknown as string,
      productId: productId,
    })
  );
};

function handleModuleTypeChange(handleModuleTypeChangeProps: IHandleModuleTypeChangeProps) {
  const {
    tableData,
    page,
    setStateData,
    setFilteredData,
    setSearchValue,
    setRecordsPerPage,
    setPageCount,
  } = handleModuleTypeChangeProps;
  setStateData(tableData);
  if (tableData.searchKey) {
    setFilteredData(tableData.searchData[page]);
    setSearchValue(tableData.searchKey);
  } else if (tableData.data[page]) {
    setFilteredData(tableData.data[page]);
    setSearchValue('');
  }
  setRecordsPerPage(tableData.recordsPerPage || RecordLength[0]);
  setPageCount(Math.ceil(tableData.totalCount / tableData.recordsPerPage) || 1);
}

function loadFormData(
  props: IMaintenanceTableProps,
  type: string,
  tableData: IStateData,
  page: number,
  recordsPerPage: number,
  searchValue: string,
  dispatch: AppDispatch
) {
  //To make an API call to store data in global state if not available or  Make an API call when it's Compare Product Hierarchy
  if (
    type === EDataMaintenanceType.CompareProductHierarchy ||
    type === EDataMaintenanceType.FeaturePropertyAssociation ||
    !tableData.data[1]
  ) {
    dispatch(
      props.getDataList({
        token: props.token,
        page: page,
        limit: recordsPerPage,
        searchValue: searchValue,
        productId: props.productId,
      })
    );
  }
}

function inverseCheckForArray(
  deleteField: string[],
  fdata: IDataTableRow,
  inverseCheckForDelete: boolean[]
) {
  let inverseCheck = false;
  for (let i = 0; i < deleteField.length; i++) {
    inverseCheck =
      inverseCheck ===
      (inverseCheckForDelete[i] ? !fdata[deleteField[i]] : Boolean(fdata[deleteField[i]]));
  }
  return inverseCheck;
}

function disableDeleteBtn(disableDeleteProps: IDisableDeleteBtnProps) {
  const {
    filteredData,
    selectionModel,
    deleteField,
    inverseCheckForDelete,
    setDisableDelete,
    stateData,
    type,
  } = disableDeleteProps;
  const nonDeleteEntries = filteredData.filter((fdata: IDataTableRow) => {
    //Enable delete button based on isDefault and isActive fields
    const innerEntries = Array.from(selectionModel.ids).filter((entry) => {
      let inverseCheck = false;
      if (Array.isArray(deleteField)) {
        inverseCheck = inverseCheckForArray(deleteField, fdata, inverseCheckForDelete as boolean[]);
      } else {
        inverseCheck = (inverseCheckForDelete as boolean)
          ? !fdata[deleteField]
          : Boolean(fdata[deleteField]);
      }
      return inverseCheck && entry === fdata['id']; //using id instead of uniqueKey as we are now assigning unique id to each and every row of data
    }).length;
    return innerEntries !== 0;
  }).length;
  if (
    (selectionModel.ids.size > 0 && nonDeleteEntries !== selectionModel.ids.size) ||
    (type === EDataTableType.Prices && stateData?.totalCount > 1)
  ) {
    setDisableDelete(false);
  } else {
    setDisableDelete(true);
  }
}

function displayCheckboxSelection(moduleType: string, type: string) {
  return (
    moduleType === EModuleType.Administration ||
    moduleType === EModuleType.Notifications ||
    moduleType === EModuleType.FeatureFlags ||
    (moduleType === EModuleType.Datamaintainance && type === EDataTableType.Languages) ||
    type === EDataTableType.Ace ||
    type === EDataTableType.Queue ||
    type === EDataTableType.Markets ||
    type === EDataTableType.Properties ||
    type === EDataTableType.MultiNumericFeature
  );
}

const getImages = (params: GridRenderCellParams, col: ITableHeaders, openDialog: IOpenDialogFn) => {
  return (
    <Stack direction="row" spacing={1}>
      {params.row[col.field].map((r: IImageData) => (
        <Chip
          size="small"
          key={r.type}
          label={r.type}
          onClick={() => openDialog(r as unknown as IDataTableRow, ECommonDialogType.Image)}
          color="primary"
          variant={r.isDefault ? 'filled' : 'outlined'}
        />
      ))}
    </Stack>
  );
};

const renderIconButton = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn
) => {
  return (
    <IconButton
      data-testid="btn-info-dialog"
      onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}
    >
      <InfoIcon color="primary" />
    </IconButton>
  );
};

//To return status icon based on status value
const getStatusIcon = (status: string, fontSize: 'small' | 'medium' | 'inherit' = 'medium') => {
  switch (status) {
    case EStatus.Success:
      return <CheckCircleIcon color="success" fontSize={fontSize} />;
    case EStatus.Failed:
      return <CancelIcon color="error" fontSize={fontSize} />;
    case EStatus.Processing:
    case EStatus.Initial:
      return <CheckCircleIcon sx={{ color: amber[500] }} fontSize={fontSize} />;
    case EStatus.New:
      return <CheckCircleIcon color="primary" fontSize={fontSize} />;
    default:
      return null;
  }
};

//To show status of each notification in table
const showStatusOfNotification = (params: GridRenderCellParams) => {
  const icon = getStatusIcon(params.row?.status);
  return <IconButton> {icon || ''} </IconButton>;
};

//To show sync status icon with different color in each notification
const InfoIconWithColor = (
  params: GridRenderCellParams,
  openDialog: IOpenDialogFn,
  col: ITableHeaders,
  status: string
) => {
  let iconColor;
  if (status === EStatusColor.Warning) {
    iconColor = amber[500];
  } else if (status === EStatusColor.Success) {
    iconColor = green[500];
  } else {
    iconColor = red[700];
  }
  return (
    <IconButton onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}>
      <InfoIcon sx={{ color: iconColor }} />
    </IconButton>
  );
};

//To show article icon
const getArticleIcon = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn
) => {
  return (
    <div>
      <IconButton onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}>
        <ArticleIcon color="primary" />
      </IconButton>
    </div>
  );
};

//To show download icon
const getDownloadIcon = (
  params: GridRenderCellParams,
  download?: (action: { productId: string; versionId: string }, dispatch: AppDispatch) => void
) => {
  if (!download) return null;
  return (
    <div>
      <IconButton
        onClick={() =>
          download(
            { productId: params.row.productCode, versionId: params.row.version },
            AppStore.dispatch
          )
        }
      >
        <FileDownloadIcon color="primary" />
      </IconButton>
    </div>
  );
};
//This method render the cell based on the ECellType
const createCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn,
  uniqueKey: string,
  type: EDataTableType,
  props: IMaintenanceTableProps
) => {
  let val = '';
  const bold = params?.row?.hasOption ? 'text-bold' : '';
  const fieldValue = params.row[col.field];
  switch (col.type) {
    case ECellType.Link:
      return (
        <button
          className="btn-plain link justify-left p-0"
          onClick={() => openDialog(params.row, type, EActionType.Edit)}
          aria-label={`Edit ${params.row[col.field]}`}
        >
          {fieldValue}
        </button>
      );
    case ECellType.Images:
      return getImages(params, col, openDialog);
    case ECellType.LongSalesText:
      return (
        <Chip
          size="small"
          key={params.row[uniqueKey]}
          label={t('labels.view')}
          onClick={() => openDialog(params.row, ECommonDialogType.LongSalesText)}
          color="primary"
          variant="outlined"
        />
      );
    case ECellType.Boolean:
      return fieldValue ? t('labels.yes') : t('labels.no');
    case ECellType.Price: {
      const locale = (countryCodes as Record<string, { locale: string }>)[
        params.row['countryCode']
      ]['locale'];
      const price = fieldValue.toLocaleString('' + locale + '-u-nu-latn', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
      return <span className="overflow-hidden overflow-ellipsis">{price}</span>;
    }
    case ECellType.ObjectChips:
      val = fieldValue
        .map((data: { code?: string; name?: string }) => data.code ?? data.name)
        .join(', ');
      return renderTooltipWithIcon(val, col, params, openDialog);
    case ECellType.ArrayChips:
      val = fieldValue ? fieldValue.join(', ') : null;
      return renderTooltipWithIcon(val, col, params, openDialog);
    case ECellType.Object:
      return (
        <span className={'overflow-hidden overflow-ellipsis'}>{getDefaultValue(fieldValue)}</span>
      );
    case ECellType.Date:
      val = formatDateString(fieldValue);
      return <span className="overflow-hidden overflow-ellipsis">{val}</span>;
    case ECellType.Update:
      return renderUpdateCell(fieldValue);
    case ECellType.Details:
      return (
        <IconButton
          data-testid="btn-info-dialog"
          onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}
        >
          <InfoIcon color="primary" />
        </IconButton>
      );
    case ECellType.Log:
      return getArticleIcon(params, col, openDialog);

    case ECellType.SyncStatus: {
      const status = renderSyncStatus(params.row.syncStatus);
      return InfoIconWithColor(params, openDialog, col, status);
    }
    case ECellType.Error: {
      if (params.row.error && params.row.error.length > 0) {
        return getArticleIcon(params, col, openDialog);
      } else {
        return null;
      }
    }
    case ECellType.Chips: {
      const hasActivities =
        params.row.activities && Object.keys(JSON.parse(params.row.activities)).length > 0;
      const retriggerDisable =
        type === EDataTableType.ProductRelease
          ? !hasActivities || ![EStatus.Success, EStatus.Failed].includes(params.row.status)
          : !params.row.isActive;

      return (
        <Chip
          size="small"
          key={params.row.code}
          onClick={() => {
            if (type === EDataTableType.ProductRelease) {
              openDialog(params.row, ECommonDialogType.RetriggerProductRelease);
            } else if (props.retrigger) {
              AppStore.dispatch(
                props.retrigger(params.row[foreignKeysForTable[type]] || params.row.code)
              );
            }
          }}
          label={t('labels.retrigger')}
          color="primary"
          variant="outlined"
          disabled={retriggerDisable}
        />
      );
    }
    case ECellType.Status: {
      return <div className="text-center"> {showStatusOfNotification(params)}</div>;
    }
    case ECellType.Refresh: {
      const searchCode = params.row[foreignKeysForTable[type]] || params.row.code;

      return (
        <IconButton
          onClick={() => {
            AppStore.dispatch(
              props.getDataList({
                searchCode: searchCode,
                caller: ECellType.Refresh,
              } as IPaginationRequest)
            );
          }}
        >
          <RefreshIcon color="primary" />
        </IconButton>
      );
    }
    case ECellType.MaxDate: {
      const datesArr = params.row[col.key as string].map(
        (data: { modifiedOn: string }) => data.modifiedOn
      );
      return <span className="overflow-hidden overflow-ellipsis">{getMaxDate(datesArr)}</span>;
    }
    case ECellType.Tooltip:
      return (
        <Tooltip title={params.row[col.field]} placement="bottom" arrow>
          <span className="overflow-hidden overflow-ellipsis">{params.row[col.field]}</span>
        </Tooltip>
      );

    case ECellType.LinearProgress: {
      return <LinearProgressBar activities={params.row[col.field]} getStatusIcon={getStatusIcon} />;
    }
    case ECellType.Download:
      return getDownloadIcon(params, props.download);
    default:
      return (
        <span className={`overflow-hidden overflow-ellipsis ${bold}`}>{params.row[col.field]}</span>
      );
  }
};

const SearchBar = (props: ISearchBarProps) => {
  const {
    stateData,
    searchValue,
    token,
    recordsPerPage,
    type,
    setSearchValue,
    setFilteredData,
    setPage,
    getDataList,
    productId,
    dispatch,
  } = props;
  const searchFieldsMessage = TableHeaders[type].main
    .filter((col) => col.searchable)
    .map((col) => t('labels.' + col.field))
    .join(', ');
  return (
    <Autocomplete
      id="product-search-box"
      clearOnBlur
      freeSolo
      value={searchValue}
      options={[]}
      className="search-bar"
      renderInput={(params) => (
        <Tooltip title={searchFieldsMessage} arrow>
          <TextField
            {...params}
            className="search-inner-text"
            placeholder={searchFieldsMessage}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Tooltip>
      )}
      onChange={(_, newVal) => {
        const trimmedVal = newVal?.trim() || '';
        setSearchValue(trimmedVal);
        handleSearch({
          newVal: trimmedVal,
          recordsPerPage,
          token,
          stateData,
          getDataList,
          setPage,
          setFilteredData,
          productId,
          dispatch,
        });
      }}
    />
  );
};

const recordLengthPicker = (
  recordsPerPage: number,
  handleChange: (event: SelectChangeEvent<number>) => void
) => {
  return (
    <div className="d-flex align-center recordPicker">
      {t('labels.recordsPerPage') + ':'}&emsp;
      <FormControl size="small" className="" style={{ width: '5.5rem' }}>
        <Select
          id="recordsPerPage"
          name="recordsPerPage"
          value={recordsPerPage}
          onChange={handleChange}
        >
          {RecordLength.map((range) => (
            <MenuItem key={range} value={range}>
              {range}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

const compareButton = (handleCompare: () => void) => {
  return (
    <>
      &emsp;
      <Button
        id="publish-button"
        variant="contained"
        onClick={handleCompare}
        startIcon={<CompareIcon id="compare-icon" />}
        className="common-btn text-capitalize"
      >
        {t('button.compare')}
      </Button>
    </>
  );
};

const ButtonSection = ({
  disableDelete,
  handleCreate,
  handleDelete,
  handleCompare,
  type,
}: IButtonSectionProps): React.JSX.Element | null => {
  if (type === EDataMaintenanceType.ProductHierarchy) {
    return compareButton(handleCompare); // Assuming compareButton is a valid component
  } else if (
    type !== EDataMaintenanceType.Queue &&
    type !== EDataMaintenanceType.ProductRelease &&
    type !== EDataMaintenanceType.ProductVersionMapping
  ) {
    return (
      <>
        &emsp;
        <Button
          data-testid="data-table-delete-button"
          className="delete-button common-btn"
          color="error"
          variant="contained"
          disabled={disableDelete}
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          {type === EDataMaintenanceType.Prices ? t('button.deleteAll') : t('button.delete')}
        </Button>
        &emsp;
        {type !== EDataMaintenanceType.Prices &&
          type !== EDataMaintenanceType.Ace &&
          type !== EDataMaintenanceType.Queue && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleCreate();
                }}
                className="text-capitalize common-btn"
              >
                {t('button.new')}
              </Button>
              {type === EDataTableType.Users && (
                <>
                  &emsp;
                  <ExportDetails />
                </>
              )}
            </>
          )}
      </>
    );
  }
  return null;
};

/**
 * Render the buttons(New and Delete ) in the data table
 * @param renderButtonSectionProps input props
 * @returns {React.JSX.Element} react element
 */
const renderButtonSection = (
  renderButtonSectionProps: IRenderButtonSectionProps
): React.JSX.Element | null => {
  const { moduleType, type, disableDelete, handleCreate, handleDelete, handleCompare } =
    renderButtonSectionProps;
  return type === EDataTableType.ProductHierarchy ||
    moduleType === EModuleType.Administration ||
    moduleType === EModuleType.FeatureFlags ||
    moduleType === EModuleType.Feature ||
    moduleType === EModuleType.Notifications ||
    (moduleType === EModuleType.Datamaintainance &&
      (type === EDataTableType.Languages || type === EDataTableType.Prices)) ||
    type === EDataTableType.Markets ||
    type === EDataTableType.Properties ? (
    <ButtonSection
      type={type}
      disableDelete={disableDelete}
      handleCreate={handleCreate}
      handleDelete={handleDelete}
      handleCompare={handleCompare}
    />
  ) : null;
};

const renderImportButton = (
  type: string,
  token: string,
  displayImport: Record<string, boolean>
) => {
  return displayImport[type] ? <FileUpload token={token} type={type} /> : null;
};

const renderNoRecordsMessage = (searchValue: string) => {
  return (
    <Stack
      direction="row"
      sx={{
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <span style={{ color: searchValue ? 'red' : 'black' }}>
        {searchValue
          ? t('messages.noSearchRecords', { searchValue: searchValue })
          : t('messages.noDataFound')}
      </span>
    </Stack>
  );
};

function CustomToolbar({
  props,
  page,
  displayImport,
  moduleType,
  disableDelete,
  handleCreate,
  handleDelete,
  handleCompare,
  handleRecordsPerPageChange,
  stateData,
  searchValue,
  token,
  recordsPerPage,
  type,
  setSearchValue,
  setFilteredData,
  setPage,
  getDataList,
  dispatch,
  productId,
}: Readonly<ICustomToolbarProps>) {
  const apiRef = useGridApiContext();
  // Get active filter count reactively - only count filters with values
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterCount =
    filterModel?.items?.filter(
      (item) => item.value !== undefined && item.value !== null && item.value !== ''
    ).length || 0;

  return (
    <Toolbar>
      <Grid className="w-100" container spacing={2}>
        <Grid className="d-flex align-center" size={6}>
          {SearchBar({
            stateData,
            searchValue,
            token,
            recordsPerPage,
            type,
            setSearchValue,
            setFilteredData,
            setPage,
            getDataList,
            dispatch,
            productId,
          })}
          &emsp;
          {recordLengthPicker(recordsPerPage, handleRecordsPerPageChange)}
          &emsp;
          <ColumnsPanelTrigger
            render={
              <ToolbarButton
                render={<Button size="small" startIcon={<GridViewColumnIcon fontSize="small" />} />}
              >
                {t('labels.columns')}
              </ToolbarButton>
            }
          />
          &emsp;
          <FilterPanelTrigger>
            <Button
              size="small"
              startIcon={
                <Badge variant="dot" color="primary" invisible={filterCount === 0}>
                  <GridFilterListIcon />
                </Badge>
              }
            >
              {t('labels.filters')}
            </Button>
          </FilterPanelTrigger>
          &emsp;
          <Button
            className="refresh-button common-btn"
            color="primary"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => {
              handleRefresh({ props, page, recordsPerPage, productId, dispatch });
            }}
          >
            {t('button.refresh')}
          </Button>
        </Grid>
        <Grid className="d-flex justify-end" size={6}>
          {renderImportButton(type, token, displayImport)}
          {renderButtonSection({
            moduleType,
            type,
            token,
            disableDelete,
            handleCreate,
            handleDelete,
            handleCompare,
          })}
        </Grid>
      </Grid>
    </Toolbar>
  );
}

const getTableClassName = (type: string) => {
  return `maintenanceTable ${type === EDataTableType.FeaturePropertyAssociation ? 'dialog-mode' : ''}`;
};

const getRowHighlighter = (type: string): string | undefined => {
  const columnDetails = TableHeaders[type].main.find((header) => header.rowHighlighter === true);
  return columnDetails?.field;
};

export const MaintenanceTable = (props: IMaintenanceTableProps) => {
  const { token, type, getDataList, moduleType } = props;
  const [stateData, setStateData] = useState<IStateData>({} as IStateData);
  const [dataGridKey, setDataGridKey] = useState(''); //Key is used for proper functionality of DataGrid(like Filter feature)
  const [filteredData, setFilteredData] = useState<IDataTableRow[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RecordLength[0]); //Default records size to be fetched from RecordLength
  const [page, setPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [disableDelete, setDisableDelete] = useState<boolean>(false);
  const tableHeaders: ITableHeaders[] =
    type === EDataTableType.PropertyAssociation ||
    type === EDataTableType.FeaturePropertyAssociation
      ? (props.columns as ITableHeaders[])
      : TableHeaders[type].main;
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const deleteField = Array.isArray(
    (deleteFields as Record<string, IDeleteFieldConfig | IDeleteFieldConfig[]>)[type]
  )
    ? (deleteFields as unknown as Record<string, IDeleteFieldConfig[]>)[type].map(
        (val: IDeleteFieldConfig) => val.field
      )
    : (deleteFields as unknown as Record<string, IDeleteFieldConfig>)[type]?.field;
  const rowHighlighter = getRowHighlighter(type);
  const inverseCheckForDelete = Array.isArray(
    (deleteFields as Record<string, IDeleteFieldConfig | IDeleteFieldConfig[]>)[type]
  )
    ? (deleteFields as unknown as Record<string, IDeleteFieldConfig[]>)[type].map(
        (val: IDeleteFieldConfig) => val.inverseCheck
      )
    : (deleteFields as unknown as Record<string, IDeleteFieldConfig>)[type]?.inverseCheck;
  const uniqueKey = foreignKeysForTable[type];
  const sortingKey = initialSortField[type];
  const productId = props.productId;
  const tableData = useSelector((state: AppState) => {
    if (moduleType === 'notifications') {
      if (type === 'queue') {
        return state.queue;
      }
      if (type === 'ace') {
        return state.notifications;
      }
      if (type === 'productRelease') {
        return state.productRelease;
      }
      if (type === 'productVersionMapping') {
        return state.productVersionMapping;
      }
    }
    return (state as unknown as Record<string, Record<string, IStateData>>)[moduleType]?.[type];
  }) as IStateData;
  const dispatch = useDispatch<AppDispatch>();

  //updated method for reduce cognitive complexities
  const openDialog = (
    data: IDataTableRow | Record<string, unknown>,
    dialogType: string,
    action?: string,
    header?: ITableHeaders
  ) => {
    switch (dialogType) {
      case ECellType.ObjectChips:
      case ECellType.ArrayChips:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            header: header,
            uniqueKey: uniqueKey,
          })
        );
        break;
      case ECellType.Details:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            header: header,
            detailsViewType: type,
            uniqueKey: uniqueKey,
          })
        );
        break;

      case EDataMaintenanceType.ProductHierarchy:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            moduleType: moduleType,
          })
        );
        break;
      case ECommonDialogType.Image:
      case ECommonDialogType.LongSalesText:

      case ECommonDialogType.ShortSalesText:

      case ECommonDialogType.Notification:

      case ECommonDialogType.DeletePrices:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
          })
        );
        break;
      case ECellType.Log:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.log')} (${(data as IDataTableRow).code || (data as IDataTableRow).id})`,
            message: props.getLogContent((data as IDataTableRow).log as string, t),
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
          })
        );
        break;
      case ECellType.SyncStatus:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.sync')} (${(data as IDataTableRow).code})`,
            message: props.getLogContent((data as IDataTableRow).syncStatus as string, t),
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
          })
        );
        break;

      case ECommonDialogType.RetriggerProductRelease:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.retrigger')}`,
            message: { id: data.id },
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
          })
        );
        break;
      case ECellType.Error:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.error')} (${(data as IDataTableRow).code})`,
            message: props.getLogContent((data as IDataTableRow).error as string, t),
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
          })
        );
        break;
      default: {
        const req = JSON.parse(JSON.stringify(Array.from(selectionModel.ids)));
        const selectedIds = filteredData
          .filter((dataSelected) => req.includes(dataSelected.id))
          .map((validData) => validData[uniqueKey]);

        const context = {
          ...data,
          searchValue,
          recordsPerPage,
          page,
          selectedIds,
        };
        const newdata = typeof data?.message === 'string' ? data.message : context;

        let title = null;
        if (type === EDataTableType.Ace) {
          title = t('header.notification');
        } else if (type === EDataTableType.Queue) {
          title = t('header.queue');
        }

        dispatch(
          setDialogMessage({
            show: true,
            title: title,
            message: data?.message ? newdata : context,
            type: dialogType,
            manipulationType: action,
            moduleType: moduleType,
            callDeleteAPI: callDeleteAPI,
          })
        );
      }
    }
  };

  const columns: GridColDef[] = createGridColumns(
    tableHeaders,
    openDialog,
    uniqueKey,
    type,
    props
  ) as GridColDef[];

  useEffect(() => {
    loadFormData(props, type, tableData, page, recordsPerPage, searchValue, dispatch);
  }, []);

  useEffect(() => {
    handleModuleTypeChange({
      tableData,
      page,
      setStateData,
      setFilteredData,
      setSearchValue,
      setRecordsPerPage,
      setPageCount,
    });
  }, [tableData]);

  useEffect(() => {
    disableDeleteBtn({
      filteredData,
      selectionModel,
      deleteField,
      inverseCheckForDelete,
      setDisableDelete,
      stateData,
      type,
    });
    setDataGridKey(searchValue + recordsPerPage + page);
  });

  const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
    const records = Number(event.target.value ?? RecordLength[0]);
    setRecordsPerPage(records);
    handleRecordsChange({
      page,
      records,
      type,
      searchValue,
      setPage,
      productId,
      props,
      dispatch,
    });
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    handlePageChange({
      value,
      recordsPerPage,
      type,
      searchValue,
      stateData,
      setFilteredData,
      productId,
      props,
      dispatch,
    });
  };

  const handleCreate = () => {
    openDialog({}, type, EActionType.Create);
  };

  /**
   * This method triggers when click on the delete button in the data table to delete the selected records
   * @returns { void }
   */
  const handleDelete = (): void => {
    const maxCount = MaxDeleteCount[type] ?? MaxDeleteCount['Default'];

    //show the error message if selected records are more than 10.
    if (selectionModel.ids.size > maxCount) {
      dispatch(
        setAlertMessage({
          show: true,
          message: t('messages.deleteLimitMessage', { limit: maxCount }),
          type: 'error',
        })
      );
    } else {
      //show the confirm dialog  message to delete the records
      openDialog({}, type, EActionType.Delete);
    }
  };

  const handleCompare = () => {
    openDialog({}, type, EActionType.Compare);
  };

  const callDeleteAPI = () => {
    const selectedIdsArray = JSON.parse(JSON.stringify(Array.from(selectionModel.ids)));
    const selectedIds = filteredData
      .filter((f) => selectedIdsArray.includes(f.id ? f.id : f.code) && (f.isActive || f.active))
      .map((f1) => f1[uniqueKey] as string);
    if (type === EDataTableType.ApiKeys) {
      const selectedKeys = selectedIds.map(
        (id) => filteredData.find((fid) => (fid.id ? fid.id : fid.code) === id)?.key as string
      );
      props.deleteItems?.(selectedIds, selectedKeys);
    } else {
      props.deleteItems?.(selectedIds);
    }
    dispatch(setDialogMessage({ show: false }));
    setSelectionModel({ type: 'include', ids: new Set() });
  };

  function displayRowStatusFailedColor(fieldValue: string) {
    return rowHighlighter && fieldValue === EStatus.Failed ? 'statusFailed' : '';
  }

  return (
    <Box data-testid="data-table" className={getTableClassName(type)}>
      <DataGrid
        key={dataGridKey}
        rows={filteredData}
        columns={columns}
        // getRowId={(row) => {
        //   //To set key to row
        //   return row['id'];
        // }}
        getRowId={(row) => row.id || row.code}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        rowHeight={35}
        columnHeaderHeight={35}
        style={{ height: '75%' }}
        initialState={{
          //To set default sort on first render
          sorting: {
            sortModel: sortingKey ? [{ field: sortingKey, sort: 'asc' }] : [],
          },
        }}
        showToolbar
        slots={{
          toolbar: () =>
            CustomToolbar({
              props,
              page,
              displayImport,
              moduleType,
              disableDelete,
              handleCreate,
              handleDelete,
              handleCompare,
              handleRecordsPerPageChange,
              stateData,
              searchValue,
              token,
              recordsPerPage,
              type,
              setSearchValue,
              setFilteredData,
              setPage,
              getDataList,
              dispatch,
              productId,
            }),
          noRowsOverlay: () => renderNoRecordsMessage(searchValue),
        }}
        slotProps={{
          columnsManagement: {
            disableShowHideToggle: true, //To hide the HIDE ALL button in Manage Columns option in Column Menu
          },
        }}
        checkboxSelection={displayCheckboxSelection(moduleType, type)}
        onRowSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
        }}
        rowSelectionModel={selectionModel}
        getRowClassName={(params) => {
          return displayRowStatusFailedColor(params.row.status);
        }}
      />
      <br />

      {filteredData?.length > 0 && (
        <Stack spacing={2} style={{ alignItems: 'center' }}>
          <Pagination
            color="primary"
            count={pageCount}
            page={page}
            onChange={handlePaginationChange}
          />
        </Stack>
      )}
    </Box>
  );
};
